import React, { useEffect, useState } from "react";
import { Table, Collapse, Row, Spin } from "antd";
import { cloneDeep, get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import Filter from "../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { langMonth, langDate } from "../../../utils/properties";
import { CheckOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import CostCentre from "../../../services/api/CostCentre";
import WorkProgressPm from "../../../services/api/WorkProgressPm";
import Supplier from "../../../services/api/Supplier";
import dayjs from "dayjs";
import Project from "../../../services/api/Project";

const ReportMaster = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const currentDate = dayjs().format("YYYYMM");
  const prevMonth = dayjs(currentDate).subtract(1, "months").endOf("month").format("YYYYMM");
  const [state, setState] = useState({
    panelContent: [],
    costCenterFilter: [],
    suppliersOptionsList: [],
    projectsOptionsList: [],
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    getCostCentre(); // Get cost centers for filter
    onSearchUserProjects(); // Get Project for filter
  }, []);

  const dataList = async (queryString) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const report = await WorkProgressPm.getPmReport(queryString);
    if (!report.error && report.data && report.data.months) {
      report.data.months.forEach((cc, i) => {
        cc.orders.forEach((ccc, j) => {
          ccc.key = `${i}${j}`; // This is necessary to remove warning in console for unique key row and I use it for open row table
        });
      });
      setState((prevState) => ({
        ...prevState,
        panelContent: get(report, "data.months", []),
      }));
    } else {
      showError(get(report, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getCostCentre = async () => {
    let dataforSelect = [];
    const cf = await CostCentre.getCostCentreFilter();
    if (!cf.errror && cf.data) {
      cf.data.forEach((cc) => {
        dataforSelect.push({
          label: `${cc.id} ${cc.name !== null ? ` | ${cc.name}` : ""}`,
          value: cc.id,
        });
      });
      setState((prevState) => ({
        ...prevState,
        costCenterFilter: dataforSelect,
      }));
    } else {
      showError(get(cf, "error.messageCode", "ER000"));
    }
  };

  const resetFilter = () => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const columns = [
    {
      title: t("report.table.oda"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("salApproval.table.costCentreId"),
      dataIndex: "costCentreId",
      key: "costCentreId",
      responsive: ["lg"],
    },
    {
      title: t("report.table.productCategoryId"),
      dataIndex: "productCategoryId",
      key: "productCategoryId",
      responsive: ["lg"],
      className: "oneline",
    },
    {
      title: t("report.table.hourlyRate"),
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      className: "table-value-right oneline",
      render: (value, row) => {
        let hourlyRate = "-";
        if (row.productCategoryType === "HOUR") {
          hourlyRate = formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          });
        }
        return hourlyRate;
      },
      responsive: ["lg"],
    },
    {
      title: t("report.table.totalOrder"),
      dataIndex: "totalOrder",
      key: "totalOrder",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.receivedAmount"),
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.residualAmount"),
      dataIndex: "residualAmount",
      key: "residualAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("report.table.netValue"),
      dataIndex: "netValue",
      key: "netValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
      responsive: ["sm"],
    },
    {
      title: t("report.table.receivedValue"),
      dataIndex: "receivedValue",
      key: "receivedValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
    },
    {
      title: t("report.table.residualValue"),
      dataIndex: "residualValue",
      key: "residualValue",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
      responsive: ["sm"],
    },
  ];

  const expandedRowRender = (dataRow) => {
    const columns = [
      {
        title: t("report.table.project"),
        dataIndex: "projectId",
        key: "projectId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectName}
            </div>
          );
        },
        responsive: ["sm"],
      },
      {
        title: t("report.table.projectTask"),
        dataIndex: "projectTaskId",
        key: "projectTaskId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectTaskName}
            </div>
          );
        },
      },
      {
        title: t("report.table.receivedAmount"),
        dataIndex: "receivedAmount",
        key: "receivedAmount",
        className: "table-value-right",
        render: (value, row) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          }),
        responsive: ["sm"],
      },
      {
        title: t("report.table.receivedValue"),
        dataIndex: "receivedValue",
        key: "receivedValue",
        className: "table-value-right",
        render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
        responsive: ["sm"],
      },
      {
        title: t("report.table.status"),
        dataIndex: "status",
        key: "status",
        responsive: ["lg"],
      },
      {
        title: t("report.table.projectStatus"),
        dataIndex: "projectStatus",
        key: "projectStatus",
        responsive: ["lg"],
      },
      {
        title: t("report.table.projectTaskStatus"),
        dataIndex: "projectTaskStatus",
        key: "projectTaskStatus",
        responsive: ["lg"],
      },
      {
        align: "center",
        title: t("report.table.enabled"),
        dataIndex: "enabled",
        key: "enabled",
        render: (value) =>
          value ? <CheckOutlined className="icon-medium" /> : <CloseOutlined className="icon-medium" />,
        responsive: ["lg"],
      },
      {
        align: "center",
        title: t("report.table.timeRegistration"),
        dataIndex: "timeRegistration",
        key: "timeRegistration",
        render: (value) =>
          value ? <CheckOutlined className="icon-medium" /> : <CloseOutlined className="icon-medium" />,
        responsive: ["sm"],
      },
    ];
    return (
      <>
        <Table columns={columns} dataSource={dataRow.projectTasks} pagination={false} />
      </>
    );
  };

  const formikFilter = useFormik({
    initialValues: {
      rangeMonth: [dayjs(prevMonth, "YYYYMM"), dayjs(currentDate, "YYYYMM")],
      costCentreId: "",
      supplierId: "",
      projectId: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.rangeMonth) {
        errors.rangeMonth = "Required!";
      }
      return errors;
    },
    onSubmit: (values) => {
      const filterSubmit = cloneDeep(values);
      delete filterSubmit.rangeMonth;
      filterSubmit.initialMonth = dayjs(values.rangeMonth[0], "YYYYMM").format("YYYYMM");
      filterSubmit.endMonth = dayjs(values.rangeMonth[1], "YYYYMM").format("YYYYMM");
      const queryString = buildQueryString(filters.obj, filterSubmit);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const changeSupplierFilter = (value) => {
    formikFilter.setFieldValue("supplierId", value);
    if (value === undefined) {
      setState((prevState) => ({ ...prevState, suppliersOptionsList: [] }));
    }
  };

  const onSearchSupplierFilter = async (value) => {
    if (value.length >= 3) {
      let dataopt = [];
      const suppliers = await Supplier.getSuppliers(`name=${value}&allPages=true`);
      if (!suppliers.error && suppliers.data) {
        suppliers.data.forEach((c) => {
          dataopt.push({ value: c.id, label: c.name });
        });
        setState((prevState) => ({
          ...prevState,
          suppliersOptionsList: dataopt,
        }));
      } else {
        showError(get(suppliers, "error.messageCode", "ER000"));
      }
    } else {
      setState((prevState) => ({ ...prevState, suppliersOptionsList: [] }));
    }
  };

  const onSearchUserProjects = async (value = "") => {
    if (value.length >= 3) {
      const userProjects = await Project.getUserProjects(`filter=${value}`);
      if (!userProjects.error && userProjects.data) {
        const selectData = [];
        userProjects.data.forEach((up) => {
          selectData.push({ label: `${up.id} | ${up.name}`, value: up.id });
        });
        setState((prevState) => ({
          ...prevState,
          projectsOptionsList: selectData,
        }));
      } else {
        showError(get(userProjects, "error.messageCode", "ER000"));
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        projectsOptionsList: [],
      }));
    }
  };

  const changeProjectIdFilter = (value) => {
    formikFilter.setFieldValue("projectId", value);
    if (value === undefined) {
      onSearchUserProjects();
    }
  };

  const fieldsFilter = [
    {
      type: "rangeMonth",
      isRequired: true,
      id: "rangeMonth",
      name: "rangeMonth",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("report.filter.rangeMonth"),
      value: formikFilter.values.rangeMonth,
      onChange: (date) => {
        formikFilter.setFieldValue("rangeMonth", date);
      },
    },
    {
      type: "select",
      isRequired: false,
      id: "costCentreId",
      name: "costCentreId",
      value: formikFilter.values.costCentreId,
      label: t("report.filter.costCenter"),
      onChange: formikFilter.handleChange("costCentreId"),
      onBlur: formikFilter.handleBlur("costCentreId"),
      dataSelect: state.costCenterFilter,
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "supplierId",
      name: "supplierId",
      value: formikFilter.values.supplierId,
      label: t("report.filter.suppliers"),
      onChange: changeSupplierFilter,
      onBlur: formikFilter.handleBlur,
      onSearch: onSearchSupplierFilter,
      dataSelect: state.suppliersOptionsList,
    },
    {
      type: "selectWithSearch",
      isRequired: false,
      id: "projectId",
      name: "projectId",
      value: formikFilter.values.projectId,
      label: t("report.filter.projectId"),
      onChange: changeProjectIdFilter,
      onBlur: formikFilter.handleBlur,
      onSearch: onSearchUserProjects,
      dataSelect: state.projectsOptionsList,
    },
  ];

  const exportReportCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await WorkProgressPm.downloadPmReport(filters.string);
    if (!exportResponse.error) {
      showSuccess();
      const blob = new Blob([exportResponse]);
      exportFile(blob, "report.csv");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const getExtra = (value) => {
    return formatNumber({
      value: value,
      lang: currentLanguage,
      suffix: "EUR",
    });
  };

  const items = [
    ...(state.panelContent && state.panelContent.length > 0
      ? state.panelContent.map((item) => ({
          key: item.id,
          label: dayjs(item.month.toString()).format(dateFormat),
          extra: getExtra(item.total),
          children: (
            <Table
              pagination={false}
              columns={columns}
              expandable={{ expandedRowRender }}
              dataSource={item.orders}
              size="small"
            />
          ),
        }))
      : []),
  ];

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={exportReportCsv}
        />
      </Row>
      <Collapse items={items} className="collapse-header" expandIconPosition="end" />
    </>
  );
};

export default ReportMaster;
